import { getPerformanceMonitorConfig, PerformanceMonitorConfig } from './performance-monitor-config';

const MAX_TIMER_VALUE = 1200000; // max. 2 minutes for a single measurement point

export interface GroupItem {
  group: any;
  durations: any;
  properties: any;
}

export class PerformanceMonitorService {
  protected enabled: boolean;
  protected url: string;
  protected started = 0;
  protected flushFailCount = 0;
  protected queue: any = {};
  private config: PerformanceMonitorConfig;
  private performance: Performance = window.performance;
  private properties: any = {};
  // private authToken: string; TODO: Is this required for Polaris?

  static readonly FLUSH_INTERVAL_TIME = 5000;
  static readonly FLUSH_REPORT_FAIL_LIMIT = 10;

  constructor(config: PerformanceMonitorConfig) {
    this.config = config;
    const isPerformanceAPIAvailable = !!(this.performance && this.performance.mark);
    const isEnabled = isPerformanceAPIAvailable && config.enabled;

    this.configure(isEnabled, config.baseUrl);
    this.setupProperties();
    this.start();
  }

  private configure(enabled: boolean, url: string) {
    const protocolIsValid = window.location.protocol.indexOf('file') === -1;

    this.enabled = !!(enabled && url && protocolIsValid);
    this.url = url;
  }

  private setupProperties() {
    this.properties = flatten({ ua: this.config.ua });
    this.properties.userlevel = this.config.userlevel;
    this.properties.abd = this.config.abd;
    this.properties.posVersion = this.config.appVersion;
  }

  private start() {
    if (!this.started && this.enabled) {
      this.started = window.setInterval(() => {
        /* istanbul ignore next else */
        if (this.enabled) {
          this.flush().then(this.onFlushSuccess(), this.onFlushFail());
        }
      }, PerformanceMonitorService.FLUSH_INTERVAL_TIME);
    }
  }

  private stop() {
    /* istanbul ignore next else */
    if (this.started) {
      window.clearInterval(this.started);
      this.started = 0;
    }
  }

  trackDuration(group: string, name: string): <T>(value?: T) => T {
    if (!this.enabled) {
      return (value?: any) => value;
    }

    if (!group) {
      throw new Error('"group" parameter is not defined');
    }

    const startLabel = `${group}_${name}_start`;
    const endLabel = `${group}_${name}_end`;

    const callback = (value?: any) => {
      this.performance.mark(endLabel);

      const startMark = this.getMark(startLabel);
      const endMark = this.getMark(endLabel);

      /* istanbul ignore next else */
      if (endMark && startMark) {
        this.writeValue(group, name, subTiming(endMark.startTime, startMark.startTime));
      }

      return value;
    };

    this.performance.mark(startLabel);

    return callback;
  }

  flush(): Promise<any> {
    const waitFor: Promise<Response>[] = [];
    const groups = this.queue;

    Object.keys(groups).forEach((group) => {
      const groupData = groups[group];

      /* istanbul ignore next else */
      if (groupData && Object.keys(groupData.durations).length > 0) {
        const data = Object.assign({}, groupData);

        groupData.durations = {};

        const requestInit: RequestInit = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'same-origin',
          body: JSON.stringify(data),
        };

        const promise = new Promise<Response>((resolve, reject) => {
          fetch(this.url, requestInit).then((response) => {
            if (response.status >= 400) {
              reject(response);
            } else {
              resolve(response);
            }
          }, reject)
            .catch(reject);
        });

        waitFor.push(promise);
      }
    });

    return Promise.all(waitFor);
  }

  private getMark(label: string): any {
    /* istanbul ignore next if */
    if (!this.enabled) {
      return null;
    }

    return this.performance
      .getEntriesByType('mark')
      .filter((item) => item.name === label)
      .reduce((acc, item) => item, null);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  protected writeValue(group: string, name: any, value?: number): void {
    let durations: any = {};

    /* istanbul ignore next if */
    if (!group) {
      return;
    }

    const groupData = this.ensureGroup(group);

    switch (typeof name) {
      /* istanbul ignore next */
      case 'object':
        durations = name;
        break;
      case 'string':
        durations[name] = value;
        break;
      /* istanbul ignore next */
      default:
        break;
    }

    groupData.durations = Object.assign({}, groupData.durations, durations);
  }

  protected ensureGroup(group: string): GroupItem {
    if (!this.queue[group]) {
      const durations = {};
      const properties = Object.assign({}, this.properties);

      this.queue[group] = {
        group,
        durations,
        properties,
      };
    }

    return this.queue[group];
  }

  private onFlushSuccess(): () => void {
    return () => {
      this.flushFailCount = 0;
    };
  }

  private onFlushFail(): () => void {
    return () => {
      this.flushFailCount = this.flushFailCount + 1;
      if (this.flushFailCount >= PerformanceMonitorService.FLUSH_REPORT_FAIL_LIMIT) {
        this.enabled = false;
        this.stop();
      }
    };
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function flatten(data: any): any {
  const result: any = {};

  function recurse(cur: any, prop: any) {
    if (Object(cur) !== cur) {
      result[prop] = cur;
    } else if (Array.isArray(cur)) {
      const len = cur.length;

      for (let i = 0; i < len; i++) {
        recurse(cur[i], prop ? `${prop}.${i}` : `${i}`);
      }
      if (len === 0) {
        result[prop] = [];
      }
    } else {
      let isEmpty = true;

      for (const key in cur) {
        isEmpty = false;
        recurse(cur[key], prop ? `${prop}.${key}` : key);
      }
      if (isEmpty) {
        result[prop] = {};
      }
    }
  }

  recurse(data, '');

  return result;
}

function negativeToMinusOne(value: number): number {
  if (typeof value !== 'number') {
    value = -1;
  }

  return value >= 0 ? value : -1;
}

function subTiming(a: number, b: number): number {
  a = negativeToMinusOne(a);
  b = negativeToMinusOne(b);

  if (a > 0 && b > 0 && a > b && (a - b) < MAX_TIMER_VALUE) {
    /* istanbul ignore next */
    return a - b;
  }

  return -1;
}

export const performanceMonitorService = new PerformanceMonitorService(getPerformanceMonitorConfig());
